import moment from 'moment';

export interface ProspectCSVData {
    status: string;
    company_name: string | null;
    civility: string;
    first_firstname: string;
    first_lastname: string;
    birthdate: string | null;
    address: {
        street: string | null;
        street2: string | null;
        city: string;
        postal_code: string;
    };
    email: string;
    mobile: string | null;
    workplace: string | null;
    
    // Second acquirer
    civility_2: string | null;
    second_firstname: string | null;
    second_lastname: string | null;
    date_of_birth_2: string | null;
    address_2: {
        street: string | null;
        street2: string | null;
        city: string | null;
        postal_code: string | null;
    };
    email_2: string | null;
    mobile_2: string | null;
    workplace_2: string | null;
    
    // Family
    family_situation: string;
    wedding_date: string | null;
    wedding_place: string | null;
    wedding_master: string | null;
    share_of_taxe_home: number | null;
    people_in_tax_home: number | null;
    childrens_in_tax_home: number | null;
    
    // Income
    first_household_reference_income_taxe: number | null;
    monthly_pay: number | null;
    job_status: string | null;
    job_since: number | null;
    real_costs: number | null;
    rent_collected: number | null;
    dividends_before_deduction: number | null;
    other_income_before_deduction: number | null;
    
    // Second acquirer income
    second_household_reference_income_taxe: number | null;
    first_household_reference_income_taxe_2: number | null;
    monthly_pay_2: number | null;
    job_status_2: string | null;
    job_since_2: number | null;
    real_costs_2: number | null;
    rent_collected_2: number | null;
    dividends_before_deduction_2: number | null;
    other_income_before_deduction_2: number | null;
    
    // Taxation
    tax_residence: string | null;
    tax_number: string | null;
    income_taxe: number | null;
    income_deduction: number | null;
    income_deduction_reason: string | null;
    tax_reduction: number | null;
    tax_reduction_reason: string | null;
    csg_crds: number | null;
    tax_credit: number | null;
    tax_before_reduction: number | null;
    real_estate_taxes: number | null;
    dom_deduction: number | null;
    
    // Second acquirer taxation
    tax_residence_c2: string | null;
    tax_number_c2: string | null;
    income_taxe_2: number | null;
    income_deduction_2: number | null;
    income_deduction_reason_2: string | null;
    tax_reduction_2: number | null;
    tax_reduction_reason_2: string | null;
    csg_crds_2: number | null;
    tax_credit_2: number | null;
    tax_before_reduction_2: number | null;
    real_estate_taxes_2: number | null;
    dom_deduction_2: number | null;
    taxes_c2: number | null;
    
    // Credit
    max_credit: number | null;
    max_credit_duration: number | null;
    rate_excluding_insurance: number | null;
    tax_chosen: string | null;
    other_conditions: string | null;
    
    // Project
    geo_zone: string | null;
    surface: number | null;
    rent: number | null;
    personnal_apport: number | null;
    lot_price: number | null;
    acquisition_date: string | null;
    notary_fees: number | null;
    investment_horizon: string | null;
    income_goals: number | null;
    capital_goals: number | null;
    retirement_goals: number | null;
    
    // Notary
    study_coordinates: string | null;
    study_contact: string | null;
    study_mobile: string | null;
    study_email: string | null;
    study_address: {
        street: string | null;
        street2: string | null;
        postal_code: string | null;
        city: string | null;
    };
}

const STATUS_MAP: { [key: string]: string } = {
    '1': 'in_progress',
    '2': 'standby',
    '3': 'processed',
    '4': 'archived',
    'En cours': 'in_progress',
    'En veille': 'standby',
    'TraitÃ©': 'processed',
    'ArchivÃ©': 'archived'
};

const CIVILITY_MAP: { [key: string]: string } = {
    '1': 'M.',
    '2': 'Mme.',
    '3': 'M. et Mme.',
    '4': 'M. et M.',
    'M.': 'M.',
    'Mme.': 'Mme.',
    'Monsieur': 'M.',
    'Madame': 'Mme.',
    'M. et Mme.': 'M. et Mme.',
    'M. et M.': 'M. et M.',
    'Mme. et Mme.': 'Mme. et Mme.',
    'Société': 'Société'
};

const FAMILY_SITUATION_MAP: { [key: string]: string } = {
    '1': 'A renseigner',
    '2': 'CÃ©libataire/DivorcÃ©',
    '3': 'CÃ©libataire/DivorcÃ© en concubinage',
    '4': 'veuf/ve avec enfant non issu du conjoint dÃ©cÃ©dÃ©',
    '5': 'veuf/ve avec enfant issu du conjoint dÃ©cÃ©dÃ©',
    '6': 'MariÃ© ou PacsÃ©'
};

const JOB_STATUS_MAP: { [key: string]: string } = {
    '1': 'SalariÃ©',
    '2': 'NC',
    'SalariÃ©': 'SalariÃ©',
    'NC': 'NC'
};

const HORIZON_MAP: { [key: string]: string } = {
    '1': '6 ans',
    '2': '9 ans',
    '3': '12 ans',
    '4': '+ de 12 ans',
    '6 ans': '6 ans',
    '9 ans': '9 ans',
    '12 ans': '12 ans',
    '+ de 12 ans': '+ de 12 ans'
};

function parseDate(dateStr: string | null): string | null {
    if (!dateStr || dateStr.trim() === '') return null;
    
    const date = moment(dateStr, ['DD/MM/YYYY', 'YYYY-MM-DD', 'MM/DD/YYYY'], true);
    return date.isValid() ? date.format('YYYY-MM-DD') : null;
}

function parseNumber(value: string | null): number | null {
    if (!value || value.trim() === '') return null;
    const cleaned = value.replace(/[^\d.-]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
}

function parseString(value: string | null): string | null {
    if (!value || value.trim() === '') return null;
    return value.trim();
}

function formatPhoneNumber(phone: string | null): string | null {
    if (!phone) return null;
    
    // Remove all non-digit characters
    let cleaned = phone.replace(/\D/g, '');
    
    // If phone starts with 33, it's French format
    if (cleaned.startsWith('33')) {
        return `(+33) ${cleaned.substring(2)}`;
    }
    
    // If phone starts with 1 and is 11 digits, it's US/Canada format
    if (cleaned.startsWith('1') && cleaned.length === 11) {
        return `(+1) ${cleaned.substring(1)}`;
    }
    
    // If phone starts with 212, it's Morocco format
    if (cleaned.startsWith('212')) {
        return `(+212) ${cleaned.substring(3)}`;
    }
    
    // If 9 digits, add leading 0 and assume French
    if (cleaned.length === 9) {
        return `(+33) 0${cleaned}`;
    }
    
    // If 10 digits and starts with 0, assume French
    if (cleaned.length === 10 && cleaned.startsWith('0')) {
        return `(+33) ${cleaned}`;
    }
    
    // If 10 digits and doesn't start with 0, assume French and add 0
    if (cleaned.length === 10 && !cleaned.startsWith('0')) {
        return `(+33) 0${cleaned}`;
    }
    
    // Default: assume French format
    return `(+33) ${cleaned}`;
}

export function parseProspectFromCSVRow(row: string[]): ProspectCSVData | null {
    if (!row || row.length === 0 || row.every(cell => !cell || cell.trim() === '')) {
        return null;
    }
    
    // Skip first column (empty/info column) - shift all indices by 1
    const status = STATUS_MAP[row[1] ? row[1].trim() : ''] || 'in_progress';
    const company_name = parseString(row[2]);
    const civility = CIVILITY_MAP[row[3] ? row[3].trim() : ''] || 'M.';
    const first_firstname = parseString(row[4]);
    const first_lastname = parseString(row[5]);
    const email = parseString(row[12]);
    
    if (!first_firstname || !first_lastname || !email) {
        return null;
    }
    
    const mobile = formatPhoneNumber(row[13]);
    const mobile_2 = formatPhoneNumber(row[24]);
    
    const prospect: ProspectCSVData = {
        status,
        company_name,
        civility,
        first_firstname,
        first_lastname,
        birthdate: parseDate(row[6]),
        address: {
            street: parseString(row[8]),
            street2: parseString(row[9]),
            city: parseString(row[10]) || '',
            postal_code: parseString(row[11]) || '',
        },
        email,
        mobile: mobile,
        workplace: parseString(row[14]),
        
        civility_2: CIVILITY_MAP[row[15] ? row[15].trim() : ''] || null,
        second_firstname: parseString(row[16]),
        second_lastname: parseString(row[17]),
        date_of_birth_2: parseDate(row[18]),
        address_2: {
            street: parseString(row[19]),
            street2: parseString(row[20]),
            postal_code: parseString(row[21]),
            city: parseString(row[22]),
        },
        email_2: parseString(row[23]),
        mobile_2: mobile_2,
        workplace_2: parseString(row[25]),
        
        family_situation: FAMILY_SITUATION_MAP[row[26] ? row[26].trim() : ''] || 'A renseigner',
        wedding_date: parseDate(row[27]),
        wedding_place: parseString(row[28]),
        wedding_master: parseString(row[29]),
        share_of_taxe_home: parseNumber(row[30]),
        people_in_tax_home: parseNumber(row[31]),
        childrens_in_tax_home: parseNumber(row[32]),
        
        first_household_reference_income_taxe: parseNumber(row[33]),
        monthly_pay: parseNumber(row[34]),
        job_status: JOB_STATUS_MAP[row[35] ? row[35].trim() : ''] || null,
        job_since: parseNumber(row[36]),
        real_costs: parseNumber(row[37]),
        rent_collected: parseNumber(row[38]),
        dividends_before_deduction: parseNumber(row[39]),
        other_income_before_deduction: parseNumber(row[40]),
        
        second_household_reference_income_taxe: parseNumber(row[41]),
        first_household_reference_income_taxe_2: parseNumber(row[42]),
        monthly_pay_2: parseNumber(row[43]),
        job_status_2: JOB_STATUS_MAP[row[44] ? row[44].trim() : ''] || null,
        job_since_2: parseNumber(row[45]),
        real_costs_2: parseNumber(row[46]),
        rent_collected_2: parseNumber(row[47]),
        dividends_before_deduction_2: parseNumber(row[48]),
        other_income_before_deduction_2: parseNumber(row[49]),
        
        tax_residence: parseString(row[50]),
        tax_number: parseString(row[51]),
        income_taxe: parseNumber(row[52]),
        income_deduction: parseNumber(row[53]),
        income_deduction_reason: parseString(row[54]),
        tax_reduction: parseNumber(row[55]),
        tax_reduction_reason: parseString(row[56]),
        csg_crds: parseNumber(row[57]),
        tax_credit: parseNumber(row[58]),
        tax_before_reduction: parseNumber(row[59]),
        real_estate_taxes: parseNumber(row[60]),
        dom_deduction: parseNumber(row[61]),
        
        tax_residence_c2: parseString(row[62]),
        tax_number_c2: parseString(row[63]),
        income_taxe_2: parseNumber(row[64]),
        income_deduction_2: parseNumber(row[65]),
        income_deduction_reason_2: parseString(row[66]),
        tax_reduction_2: parseNumber(row[67]),
        tax_reduction_reason_2: parseString(row[68]),
        csg_crds_2: parseNumber(row[69]),
        tax_credit_2: parseNumber(row[70]),
        tax_before_reduction_2: parseNumber(row[71]),
        real_estate_taxes_2: parseNumber(row[72]),
        dom_deduction_2: parseNumber(row[73]),
        taxes_c2: parseNumber(row[74]),
        
        max_credit: parseNumber(row[75]),
        max_credit_duration: parseNumber(row[76]),
        rate_excluding_insurance: parseNumber(row[77]),
        tax_chosen: parseString(row[78]),
        other_conditions: parseString(row[79]),
        
        geo_zone: parseString(row[80]),
        surface: parseNumber(row[81]),
        rent: parseNumber(row[82]),
        personnal_apport: parseNumber(row[83]),
        lot_price: parseNumber(row[84]),
        acquisition_date: parseDate(row[85]),
        notary_fees: parseNumber(row[86]),
        investment_horizon: HORIZON_MAP[row[87] ? row[87].trim() : ''] || null,
        income_goals: parseNumber(row[88]),
        capital_goals: parseNumber(row[89]),
        retirement_goals: parseNumber(row[90]),
        
        study_coordinates: parseString(row[91]),
        study_contact: parseString(row[92]),
        study_mobile: parseString(row[93]),
        study_email: parseString(row[94]),
        study_address: {
            street: parseString(row[95]),
            street2: parseString(row[96]),
            postal_code: parseString(row[97]),
            city: parseString(row[98]),
        },
    };
    
    return prospect;
}

export function parseCSVFile(csvContent: string): ProspectCSVData[] {
    const lines = csvContent.split('\n');
    const prospects: ProspectCSVData[] = [];
    
    // Start from line 5 (index 4) as data starts there
    for (let i = 4; i < lines.length; i++) {
        const line = lines[i];
        if (!line || line.trim() === '') continue;
        
        // Parse CSV line respecting quotes
        const row: string[] = [];
        let currentCell = '';
        let insideQuotes = false;
        
        for (let j = 0; j < line.length; j++) {
            const char = line[j];
            
            if (char === '"') {
                insideQuotes = !insideQuotes;
            } else if (char === ',' && !insideQuotes) {
                row.push(currentCell.trim());
                currentCell = '';
            } else {
                currentCell += char;
            }
        }
        row.push(currentCell.trim());
        
        const prospect = parseProspectFromCSVRow(row);
        if (prospect) {
            prospects.push(prospect);
        }
    }
    
    return prospects;
}